import React from 'react';
import { motion } from 'motion/react';
import { WHY_CHOOSE_US } from '../data/companyData';
import { Users, Sparkles, ShieldCheck, Zap, Globe, Headphones, ArrowRight, FileCheck, Lock, Award, CheckCircle2, Shield } from 'lucide-react';

interface WhyChooseUsProps {
  onOpenEstimator: () => void;
}

const COMPLIANCE_BADGES = [
  {
    id: 'pdf-ua',
    title: 'PDF/UA-1 & WCAG 2.1 AAA',
    standard: 'ISO 14289-1 Accessibility Standard',
    description: 'Universal accessibility compliance. PAC 2021 & Matterhorn Protocol audited with logical reading order, tag trees, and screen reader optimization.',
    badgeText: 'ISO 14289-1 Compliant',
    tagColor: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    borderColor: 'hover:border-emerald-500/60',
    icon: FileCheck,
    highlights: ['PAC 2021 Verified', 'Matterhorn 100% Pass', 'Screen Reader Ready']
  },
  {
    id: 'jats-xml',
    title: 'JATS 1.3 / BITS 2.1 XML',
    standard: 'NLM Archival & Exchange Schema',
    description: '100% valid XML compliant with NLM DTDs, PubMed Central, Crossref, HighWire Press, and STS standards for journals and books.',
    badgeText: 'JATS / BITS DTD Validated',
    tagColor: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    borderColor: 'hover:border-blue-500/60',
    icon: Award,
    highlights: ['NLM DTD Compliant', 'PubMed Central Ready', 'Crossref DOI Encoded']
  },
  {
    id: 'iso-security',
    title: 'ISO 27001 & Data Protection',
    standard: 'Manuscript IP & Confidentiality Security',
    description: 'Bank-grade AES-256 encrypted transfer, air-gapped manuscript vaults, strict enterprise NDAs, and HIPAA/GDPR-aligned data controls.',
    badgeText: 'AES-256 Encrypted & NDA Protected',
    tagColor: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    borderColor: 'hover:border-purple-500/60',
    icon: Lock,
    highlights: ['AES-256 Encryption', 'Strict Client NDA', 'Secure Vault Storage']
  },
  {
    id: 'quality-iso',
    title: 'ISO 9001:2015 Quality System',
    standard: 'Zero-Defect Prepress Quality Assurance',
    description: 'Multi-stage automated DTD preflighting, double-pass expert proofing, and 99.98% first-pass accuracy across high-volume layouts.',
    badgeText: 'Quality Assurance Verified',
    tagColor: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    borderColor: 'hover:border-amber-500/60',
    icon: ShieldCheck,
    highlights: ['Double-Pass Proofing', 'Automated Preflight', '99.98% Accuracy']
  }
];

export const WhyChooseUs: React.FC<WhyChooseUsProps> = ({ onOpenEstimator }) => {
  const getIcon = (name: string) => {
    switch (name) {
      case 'Users': return Users;
      case 'Sparkles': return Sparkles;
      case 'ShieldCheck': return ShieldCheck;
      case 'Zap': return Zap;
      case 'Globe': return Globe;
      default: return Headphones;
    }
  };

  return (
    <section id="why-us" className="py-16 sm:py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Background Subtle Gradient Blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center max-w-3xl mx-auto space-y-4"
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-wider">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-400" /> Enterprise Advantage
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
            Why Leading Publishers Trust ASAP LEARNING
          </h2>
          <p className="text-slate-300 text-base sm:text-lg">
            We combine deep publishing domain mastery with intelligent automation to deliver press-ready assets faster, error-free, and at optimal costs.
          </p>
        </motion.div>

        {/* 6 Cards Grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {WHY_CHOOSE_US.map((item, idx) => {
            const IconComp = getIcon(item.iconName);
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 35, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.5, delay: idx * 0.08, ease: 'easeOut' }}
                className="bg-slate-800/80 rounded-2xl p-7 border border-slate-700/80 hover:border-blue-500/80 hover:bg-slate-800 transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                    <IconComp className="w-6 h-6" />
                  </div>
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-slate-700/80 text-blue-300 border border-slate-600">
                    {item.stat}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-300 transition-colors">
                  {item.title}
                </h3>

                <p className="text-xs text-purple-300 font-semibold mb-3">
                  ✦ {item.highlight}
                </p>

                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* SECURITY & COMPLIANCE STANDARDS SHOWCASE */}
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
          className="mt-16 pt-12 border-t border-slate-800"
        >
          <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-indigo-400 text-xs font-bold uppercase tracking-wider">
              <Shield className="w-3.5 h-3.5 text-indigo-400" />
              <span>Certified Standards & Compliance</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              Security, Quality & Compliance Standards
            </h3>
            <p className="text-xs sm:text-sm text-slate-300">
              Our automated workflows adhere strictly to international publishing protocols, XML schemas, and IP security standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {COMPLIANCE_BADGES.map((badge) => {
              const BadgeIcon = badge.icon;
              return (
                <div
                  key={badge.id}
                  className={`p-6 rounded-2xl bg-slate-950/80 border border-slate-800 ${badge.borderColor} transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1`}
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                        <BadgeIcon className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase border ${badge.tagColor}`}>
                        Verified
                      </span>
                    </div>

                    <div>
                      <h4 className="text-base font-extrabold text-white leading-snug group-hover:text-blue-300 transition-colors">
                        {badge.title}
                      </h4>
                      <p className="text-[11px] font-semibold text-slate-300 mt-0.5">
                        {badge.standard}
                      </p>
                    </div>

                    <p className="text-xs text-slate-300 leading-relaxed">
                      {badge.description}
                    </p>
                  </div>

                  <div className="mt-5 pt-4 border-t border-slate-800/80 space-y-1.5">
                    {badge.highlights.map((item, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[11px] font-bold text-slate-300">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Bottom Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900 border border-blue-500/30 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div>
            <h3 className="text-xl font-bold text-white">Require a Custom Sample or Prepress Audit?</h3>
            <p className="text-xs sm:text-sm text-blue-200 mt-1">We convert sample chapters and test PDF/UA tagging free of charge within 24 hours.</p>
          </div>
          <button
            onClick={onOpenEstimator}
            className="px-6 py-3 rounded-xl bg-white text-blue-900 font-bold text-xs sm:text-sm hover:bg-blue-50 shadow-lg transition-all shrink-0 flex items-center gap-2"
          >
            <span>Run Free Project Assessment</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>

      </div>
    </section>
  );
};

